import React, { useState, useCallback } from 'react';
import {
  Typography, Box, Paper, TextField, Button, Alert, CircularProgress,
  Table, TableHead, TableBody, TableRow, TableCell, Chip, Stack,
  Dialog, DialogTitle, DialogContent, DialogActions, Stepper, Step, StepLabel,
  Card, CardContent, InputAdornment, Radio,
} from '@mui/material';
import {
  Search as SearchIcon,
  MergeType as MergeIcon,
  CheckCircle as SuccessIcon,
  Warning as WarningIcon,
  PeopleAlt as DuplicatesIcon,
} from '@mui/icons-material';
import AdminLayout from '../../../layouts/AdminLayout';
import unifiedApi from '../../../utils/unifiedApi';
import { useIsSuperAdmin } from '../../../contexts/PermissionsContext';

const steps = ['Select Users', 'Review Changes', 'Confirm & Execute'];

const MergeUsersPage = () => {
  const isSuperAdmin = useIsSuperAdmin();

  // Step state
  const [activeStep, setActiveStep] = useState(0);

  // Search state
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchError, setSearchError] = useState(null);

  // Selection state
  const [survivor, setSurvivor] = useState(null);
  const [victim, setVictim] = useState(null);

  // Preview state
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState(null);

  // Execute state
  const [result, setResult] = useState(null);
  const [executeLoading, setExecuteLoading] = useState(false);
  const [executeError, setExecuteError] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);

  // Duplicates state
  const [duplicateGroups, setDuplicateGroups] = useState([]);
  const [duplicatesLoading, setDuplicatesLoading] = useState(false);
  const [duplicatesError, setDuplicatesError] = useState(null);
  const [showDuplicates, setShowDuplicates] = useState(false);

  const handleSearch = useCallback(async () => {
    if (!searchQuery || searchQuery.trim().length < 2) return;
    setSearchLoading(true);
    setSearchError(null);
    try {
      const data = await unifiedApi.searchUsersForMerge(searchQuery.trim());
      setSearchResults(data || []);
    } catch (err) {
      setSearchError(err.message || 'Search failed');
    } finally {
      setSearchLoading(false);
    }
  }, [searchQuery]);

  const handleSearchKeyDown = (e) => {
    if (e.key === 'Enter') handleSearch();
  };

  const handleFindDuplicates = useCallback(async () => {
    setDuplicatesLoading(true);
    setDuplicatesError(null);
    try {
      const data = await unifiedApi.findLikelyDuplicates();
      setDuplicateGroups(data || []);
      setShowDuplicates(true);
    } catch (err) {
      setDuplicatesError(err.message || 'Failed to find duplicates');
    } finally {
      setDuplicatesLoading(false);
    }
  }, []);

  const handleSelectDuplicateGroup = (group) => {
    if (group.members.length >= 2) {
      setSurvivor(group.members[0]);
      setVictim(group.members[1]);
      setSearchResults(group.members);
      setShowDuplicates(false);
    }
  };

  const handleSelectSurvivor = (user) => {
    setSurvivor(user);
    if (victim?.userTenantId === user.userTenantId) setVictim(null);
  };

  const handleSelectVictim = (user) => {
    setVictim(user);
    if (survivor?.userTenantId === user.userTenantId) setSurvivor(null);
  };

  const handlePreview = useCallback(async () => {
    if (!survivor || !victim) return;
    setPreviewLoading(true);
    setPreviewError(null);
    try {
      const data = await unifiedApi.previewUserMerge(survivor.userTenantId, victim.userTenantId);
      setPreview(data);
      setActiveStep(1);
    } catch (err) {
      setPreviewError(err.message || 'Preview failed');
    } finally {
      setPreviewLoading(false);
    }
  }, [survivor, victim]);

  const handleExecute = useCallback(async () => {
    setConfirmOpen(false);
    setExecuteLoading(true);
    setExecuteError(null);
    try {
      const data = await unifiedApi.executeUserMerge(survivor.userTenantId, victim.userTenantId);
      setResult(data);
      setActiveStep(2);
    } catch (err) {
      setExecuteError(err.message || 'Merge failed');
    } finally {
      setExecuteLoading(false);
    }
  }, [survivor, victim]);

  const handleReset = () => {
    setActiveStep(0);
    setSearchQuery('');
    setSearchResults([]);
    setSurvivor(null);
    setVictim(null);
    setPreview(null);
    setResult(null);
    setSearchError(null);
    setPreviewError(null);
    setExecuteError(null);
    setDuplicateGroups([]);
    setDuplicatesError(null);
    setShowDuplicates(false);
  };

  if (!isSuperAdmin) {
    return (
      <AdminLayout>
        <Box sx={{ p: 3 }}>
          <Alert severity="error">You must be a Super Admin to access this page.</Alert>
        </Box>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1200, mx: 'auto' }}>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 3 }}>
          <MergeIcon color="primary" />
          <Typography variant="h5" fontWeight="bold">Merge User Accounts</Typography>
        </Stack>

        <Alert severity="warning" sx={{ mb: 3 }}>
          This operation is irreversible. All records from the victim account will be transferred to the survivor account. 
          The victim account will be permanently deleted.
        </Alert>

        <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {/* Step 1: Select Users */}
        {activeStep === 0 && (
          <>
            <Paper sx={{ p: 3, mb: 3 }}>
              <Typography variant="h6" sx={{ mb: 2 }}>Search Members</Typography>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 2 }}>
                <TextField
                  fullWidth
                  size="small"
                  placeholder="Search by name or email (min 2 characters)"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyDown={handleSearchKeyDown}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon />
                      </InputAdornment>
                    ),
                  }}
                />
                <Button
                  variant="contained"
                  onClick={handleSearch}
                  disabled={searchLoading || searchQuery.trim().length < 2}
                  sx={{ minWidth: 100 }}
                >
                  {searchLoading ? <CircularProgress size={24} /> : 'Search'}
                </Button>
                <Button
                  variant="outlined"
                  onClick={handleFindDuplicates}
                  disabled={duplicatesLoading}
                  startIcon={duplicatesLoading ? <CircularProgress size={20} /> : <DuplicatesIcon />}
                  sx={{ minWidth: 180 }}
                >
                  Find Likely Duplicates
                </Button>
              </Stack>

              {searchError && <Alert severity="error" sx={{ mb: 2 }}>{searchError}</Alert>}
              {duplicatesError && <Alert severity="error" sx={{ mb: 2 }}>{duplicatesError}</Alert>}

              {showDuplicates && (
                <Box sx={{ mb: 3 }}>
                  {duplicateGroups.length === 0 ? (
                    <Alert severity="success">No likely duplicates found in this tenant.</Alert>
                  ) : (
                    <>
                      <Alert severity="info" sx={{ mb: 2 }}>
                        Found {duplicateGroups.length} potential duplicate group{duplicateGroups.length !== 1 ? 's' : ''}.
                        Select a group to populate the survivor and victim for merging.
                      </Alert>
                      {duplicateGroups.map((group) => (
                        <Paper
                          key={`${group.matchReason}-${group.members.map((m) => m.userTenantId).join('-')}`}
                          variant="outlined"
                          sx={{ p: 2, mb: 2 }}
                        >
                          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                            <Chip label={group.matchReason} size="small" color="warning" clickable={false} onClick={() => {}} />
                            <Button
                              size="small"
                              variant="outlined"
                              onClick={() => handleSelectDuplicateGroup(group)}
                            >
                              Select for Merge
                            </Button>
                          </Stack>
                          <Box sx={{ width: '100%', overflowX: 'auto' }}>
                            <Table size="small">
                              <TableHead>
                              <TableRow>
                                <TableCell>Name</TableCell>
                                <TableCell>Email</TableCell>
                                <TableCell>Teams</TableCell>
                                <TableCell>Roles</TableCell>
                                <TableCell>UserTenant ID</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {group.members.map((member) => (
                                <TableRow key={member.userTenantId}>
                                  <TableCell>{member.firstName} {member.lastName}</TableCell>
                                  <TableCell>{member.email}</TableCell>
                                  <TableCell>
                                    <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                                      {member.teams.map((t) => (
                                        <Chip key={t} label={t} size="small" clickable={false} onClick={() => {}} />
                                      ))}
                                    </Stack>
                                  </TableCell>
                                  <TableCell>
                                    <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                                      {member.roles.map((r) => (
                                        <Chip key={r} label={r} size="small" color="secondary" clickable={false} onClick={() => {}} />
                                      ))}
                                    </Stack>
                                  </TableCell>
                                  <TableCell>{member.userTenantId}</TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                          </Box>
                        </Paper>
                      ))}
                    </>
                  )}
                </Box>
              )}

              {searchResults.length > 0 && (
                <Box sx={{ overflowX: 'auto' }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Survivor</TableCell>
                        <TableCell>Victim</TableCell>
                        <TableCell>Name</TableCell>
                        <TableCell>Email</TableCell>
                        <TableCell>Teams</TableCell>
                        <TableCell>Roles</TableCell>
                        <TableCell>UserTenant ID</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {searchResults.map((user) => (
                        <TableRow key={user.userTenantId} hover>
                          <TableCell>
                            <Radio
                              checked={survivor?.userTenantId === user.userTenantId}
                              onChange={() => handleSelectSurvivor(user)}
                              size="small"
                            />
                          </TableCell>
                          <TableCell>
                            <Radio
                              checked={victim?.userTenantId === user.userTenantId}
                              onChange={() => handleSelectVictim(user)}
                              size="small"
                            />
                          </TableCell>
                          <TableCell>{user.firstName} {user.lastName}</TableCell>
                          <TableCell>{user.email}</TableCell>
                          <TableCell>
                            <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                              {user.teams.map((t) => (
                                <Chip key={t} label={t} size="small" clickable={false} onClick={() => {}} />
                              ))}
                            </Stack>
                          </TableCell>
                          <TableCell>
                            <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                              {user.roles.map((r) => (
                                <Chip key={r} label={r} size="small" color="secondary" clickable={false} onClick={() => {}} />
                              ))}
                            </Stack>
                          </TableCell>
                          <TableCell>{user.userTenantId}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </Box>
              )}
            </Paper>

            {/* Selection Summary */}
            {(survivor || victim) && (
              <Paper sx={{ p: 3, mb: 3 }}>
                <Typography variant="h6" sx={{ mb: 2 }}>Selected Users</Typography>
                <Stack direction={{ xs: 'column', md: 'row' }} spacing={3}>
                  <UserSummaryCard label="Survivor (Keep)" user={survivor} color="success" />
                  <UserSummaryCard label="Victim (Delete)" user={victim} color="error" />
                </Stack>
              </Paper>
            )}

            {previewError && <Alert severity="error" sx={{ mb: 2 }}>{previewError}</Alert>}

            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Button
                variant="contained"
                onClick={handlePreview}
                disabled={!survivor || !victim || previewLoading}
                startIcon={previewLoading ? <CircularProgress size={20} /> : null}
              >
                Preview Merge
              </Button>
            </Box>
          </>
        )}

        {/* Step 2: Review Changes */}
        {activeStep === 1 && preview && (
          <>
            <Paper sx={{ p: 3, mb: 3 }}>
              <Typography variant="h6" sx={{ mb: 2 }}>Merge Preview</Typography>
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={3} sx={{ mb: 3 }}>
                <UserSummaryCard label="Survivor (Keep)" user={preview.survivor} color="success" />
                <UserSummaryCard label="Victim (Delete)" user={preview.victim} color="error" />
              </Stack>

              <Alert severity="info" sx={{ mb: 3 }}>{preview.summary}</Alert>

              <Stack direction="row" spacing={3} sx={{ mb: 3 }}>
                <Chip label={`${preview.totalTransfers} Transfer(s)`} color="primary" clickable={false} onClick={() => {}} />
                <Chip label={`${preview.totalSkips} Skip(s)`} color="warning" clickable={false} onClick={() => {}} />
                <Chip label={`${preview.totalDeletes} Delete(s)`} color="error" clickable={false} onClick={() => {}} />
              </Stack>

              {preview.willDeleteVictimUser && (
                <Alert severity="warning" sx={{ mb: 3 }}>
                  The victim&apos;s global User account will be permanently deleted (no other tenant memberships found).
                </Alert>
              )}

              {/* Detailed table-by-table breakdown */}
              <Box sx={{ overflowX: 'auto' }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell><strong>Table</strong></TableCell>
                      <TableCell align="right"><strong>Transfer</strong></TableCell>
                      <TableCell align="right"><strong>Skip</strong></TableCell>
                      <TableCell align="right"><strong>Delete</strong></TableCell>
                      <TableCell><strong>Details</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {preview.tablePreviews.map((table) => (
                      <TableRow key={table.tableName}>
                        <TableCell><strong>{table.tableName}</strong></TableCell>
                        <TableCell align="right">
                          {table.transferCount > 0 ? (
                            <Chip label={table.transferCount} size="small" color="primary" clickable={false} onClick={() => {}} />
                          ) : 0}
                        </TableCell>
                        <TableCell align="right">
                          {table.skipCount > 0 ? (
                            <Chip label={table.skipCount} size="small" color="warning" clickable={false} onClick={() => {}} />
                          ) : 0}
                        </TableCell>
                        <TableCell align="right">
                          {table.deleteCount > 0 ? (
                            <Chip label={table.deleteCount} size="small" color="error" clickable={false} onClick={() => {}} />
                          ) : 0}
                        </TableCell>
                        <TableCell>
                          <Stack spacing={0.5}>
                            {table.details.map((detail, i) => (
                              <Typography key={i} variant="body2" color="text.secondary">{detail}</Typography>
                            ))}
                          </Stack>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Box>
            </Paper>

            {executeError && <Alert severity="error" sx={{ mb: 2 }}>{executeError}</Alert>}

            <Stack direction="row" justifyContent="space-between">
              <Button onClick={() => setActiveStep(0)}>Back</Button>
              <Button
                variant="contained"
                color="error"
                onClick={() => setConfirmOpen(true)}
                disabled={executeLoading}
                startIcon={executeLoading ? <CircularProgress size={20} /> : <WarningIcon />}
              >
                Execute Merge
              </Button>
            </Stack>
          </>
        )}

        {/* Step 3: Result */}
        {activeStep === 2 && result && (
          <>
            <Paper sx={{ p: 3, mb: 3 }}>
              <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
                <SuccessIcon color="success" />
                <Typography variant="h6">Merge Completed</Typography>
              </Stack>

              <Alert severity="success" sx={{ mb: 3 }}>{result.summary}</Alert>

              <Stack direction="row" spacing={3} sx={{ mb: 3 }}>
                <Chip label={`${result.totalTransfers} Transferred`} color="primary" clickable={false} onClick={() => {}} />
                <Chip label={`${result.totalSkips} Skipped`} color="warning" clickable={false} onClick={() => {}} />
                <Chip label={`${result.totalDeletes} Deleted`} color="error" clickable={false} onClick={() => {}} />
              </Stack>

              {result.victimUserDeleted && (
                <Alert severity="info" sx={{ mb: 3 }}>Victim&apos;s global User account was deleted.</Alert>
              )}

              <Box sx={{ overflowX: 'auto' }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell><strong>Table</strong></TableCell>
                      <TableCell align="right"><strong>Transfer</strong></TableCell>
                      <TableCell align="right"><strong>Skip</strong></TableCell>
                      <TableCell align="right"><strong>Delete</strong></TableCell>
                      <TableCell><strong>Details</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {result.tableResults.map((table) => (
                      <TableRow key={table.tableName}>
                        <TableCell><strong>{table.tableName}</strong></TableCell>
                        <TableCell align="right">{table.transferCount}</TableCell>
                        <TableCell align="right">{table.skipCount}</TableCell>
                        <TableCell align="right">{table.deleteCount}</TableCell>
                        <TableCell>
                          <Stack spacing={0.5}>
                            {table.details.map((detail, i) => (
                              <Typography key={i} variant="body2" color="text.secondary">{detail}</Typography>
                            ))}
                          </Stack>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Box>
            </Paper>

            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Button variant="contained" onClick={handleReset}>Merge Another</Button>
            </Box>
          </>
        )}

        {/* Confirmation Dialog */}
        <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>Confirm Merge</DialogTitle>
          <DialogContent>
            <Alert severity="error" sx={{ mb: 2 }}>
              This action cannot be undone. All records from the victim account will be permanently transferred to the survivor account.
            </Alert>
            <Typography>
              <strong>Survivor:</strong> {survivor?.firstName} {survivor?.lastName} ({survivor?.email})
            </Typography>
            <Typography>
              <strong>Victim:</strong> {victim?.firstName} {victim?.lastName} ({victim?.email})
            </Typography>
            <Typography sx={{ mt: 1 }}>
              <strong>{preview?.totalTransfers}</strong> records will be transferred, <strong>{preview?.totalSkips}</strong> duplicates skipped, <strong>{preview?.totalDeletes}</strong> records deleted.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setConfirmOpen(false)}>Cancel</Button>
            <Button variant="contained" color="error" onClick={handleExecute}>
              Confirm Merge
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </AdminLayout>
  );
};

const UserSummaryCard = ({ label, user, color }) => {
  if (!user) {
    return (
      <Card sx={{ flex: 1, opacity: 0.5 }}>
        <CardContent>
          <Typography variant="subtitle2" color={`${color}.main`}>{label}</Typography>
          <Typography variant="body2" color="text.secondary">Not selected</Typography>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card sx={{ flex: 1, borderLeft: 3, borderColor: `${color}.main` }}>
      <CardContent>
        <Typography variant="subtitle2" color={`${color}.main`} sx={{ mb: 1 }}>{label}</Typography>
        <Typography variant="body1" fontWeight="bold">{user.firstName} {user.lastName}</Typography>
        <Typography variant="body2" color="text.secondary">{user.email}</Typography>
        <Typography variant="caption" color="text.secondary">UserTenant ID: {user.userTenantId}</Typography>
        {user.teams?.length > 0 && (
          <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ mt: 1 }}>
            {user.teams.map((t) => (
              <Chip key={t} label={t} size="small" clickable={false} onClick={() => {}} />
            ))}
          </Stack>
        )}
        {user.roles?.length > 0 && (
          <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ mt: 0.5 }}>
            {user.roles.map((r) => (
              <Chip key={r} label={r} size="small" color="secondary" clickable={false} onClick={() => {}} />
            ))}
          </Stack>
        )}
      </CardContent>
    </Card>
  );
};

export default MergeUsersPage;
